import { type Goal } from '@fin/shared';


import { useState } from 'react';
import { Button, ConfirmButton, Field, Icon, Input, Loading, Modal, Money, NumberInput, Progress, Section, Select, useAction } from '../../components/ui.tsx';
import { del, patch, post } from '../../lib/api.ts';
import { useGoals, useWallets } from '../../lib/data.ts';
import { dateLong, pct } from '../../lib/format.ts';
import { invalidate } from '../../lib/query.ts';



export function GoalsSection() {
  const q = useGoals();
  const [edit, setEdit] = useState<Goal | 'new' | null>(null);
  return (
    <Section title="Цели" aside={<Button size="sm" onClick={() => setEdit('new')}><Icon name="plus" size={16} /> Цель</Button>}>
      {!q.data ? <Loading /> : (
        <ul className="goals">
          {q.data.filter((g) => !g.archived).map((g) => (
            <li key={g.id}>
              <button type="button" className="goal-btn" onClick={() => setEdit(g)}>
                <div className="goal-line"><strong>{g.name}</strong><span><Money value={g.current} currency={g.currency} /> из <Money value={g.targetAmount} currency={g.currency} /></span></div>
                <Progress value={g.progress} tone="sky" label={g.name} />
                <span className="muted small">{pct(g.progress)}{g.deadline ? `, к ${dateLong(g.deadline, true)}` : ''}{g.walletId ? ', считается по кошельку' : ''}</span>
              </button>
            </li>
          ))}
          {!q.data.length ? <li className="muted">Целей пока нет.</li> : null}
        </ul>
      )}
      {edit ? <GoalModal goal={edit === 'new' ? undefined : edit} onClose={() => setEdit(null)} /> : null}
    </Section>
  );
}

export function GoalModal({ goal, onClose }: { goal?: Goal; onClose: () => void }) {
  const wallets = useWallets();
  const [name, setName] = useState(goal?.name ?? '');
  const [target, setTarget] = useState<number | null>(goal?.targetAmount ?? null);
  const [walletId, setWalletId] = useState<number | ''>(goal?.walletId ?? '');
  const [manual, setManual] = useState<number | null>(goal?.manualAmount ?? 0);
  const [deadline, setDeadline] = useState(goal?.deadline ?? '');
  const [archived, setArchived] = useState(goal?.archived ?? false);
  const { busy, run } = useAction();
  const save = () => run(async () => {
    const w = wallets.data?.find((x) => x.id === walletId);
    const body = { name, targetAmount: target, currency: w?.currency ?? goal?.currency ?? 'RUB', walletId: walletId || null, manualAmount: manual ?? 0, deadline: deadline || null, archived };
    if (goal) await patch(`/goals/${goal.id}`, body); else await post('/goals', body);
    invalidate('/goals', '/dashboard');
    onClose();
  }, 'Цель сохранена');
  return (
    <Modal className="modal-form" title={goal ? goal.name : 'Новая цель'} onClose={onClose} footer={<>
      {goal ? <ConfirmButton size="md" onConfirm={async () => { await del(`/goals/${goal.id}`); invalidate('/goals', '/dashboard'); onClose(); }}>Удалить</ConfirmButton> : <span />}
      <div className="row-gap"><Button onClick={onClose}>Отмена</Button><Button variant="primary" busy={busy} onClick={save}>Сохранить</Button></div>
    </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <Field label="Название"><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus required /></Field>
        <Field label="Цель, сумма"><NumberInput value={target} onChange={setTarget} /></Field>
        <Field label="Считать по кошельку" hint="Прогресс = остаток кошелька. Без кошелька — сумма вручную.">
          <Select value={walletId} onChange={(e) => setWalletId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Вручную</option>
            {(wallets.data ?? []).filter((w) => !w.archived).map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </Select>
        </Field>
        {!walletId ? <Field label="Накоплено"><NumberInput value={manual} onChange={setManual} /></Field> : null}
        <Field label="Срок"><Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} /></Field>
        {goal ? <label className="check"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> В архиве</label> : null}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
